import {messages} from '@smartmixin/i18n';

import {EMoonPhase} from './phases';

/**
 * `EMoonPhase` to its label, shared by both apps.
 *
 * A `Record` keyed by the enum, not an if-chain. The chain this ultimately
 * replaces had no final `else`, so an unmatched value returned `undefined` and
 * `formatMessage(undefined)` threw; a `Record` makes the same omission a
 * compile error instead. `EMoonPhase.COUNT` is excluded deliberately — it is a
 * length marker, not a phase — which is why the lookup still needs a fallback.
 *
 * Returns the `MessageDescriptor` rather than taking `formatMessage` and
 * returning a string, which is where this differs from
 * `ForecastMap/builderLabels.ts`. That module needs the callback because it
 * falls back to a *raw identifier* and so has to format conditionally; here
 * every branch is a descriptor, and returning it keeps the call sites as
 * `formatMessage(Moon.getPhaseMessage(phase))` — unchanged from before the move.
 *
 * The webapp copy this replaces carried a docstring asserting that it could not
 * live here, because "`@smartmixin/core` deliberately does not depend on
 * [i18n]". That was untrue — twelve core modules import it,
 * `ForecastMap/builderLabels.ts` among them. (The mobile copy made no such
 * claim; it simply existed.) The alternative that docstring suggested, moving it
 * into `packages/i18n`, is the one option that genuinely does not work: i18n
 * builds to `dist/` via `tsc`, so importing `EMoonPhase` from core there would
 * create a package cycle and drag core's source into i18n's build output.
 */
const PHASE_MESSAGES: Record<
  Exclude<EMoonPhase, EMoonPhase.COUNT>,
  (typeof messages.astral)[keyof typeof messages.astral]
> = {
  [EMoonPhase.New]: messages.astral.newMoon,
  [EMoonPhase.WaxingCrescent]: messages.astral.waxingCrescent,
  [EMoonPhase.QuarterMoon]: messages.astral.firstQuarter,
  [EMoonPhase.WaxingGibbous]: messages.astral.waxingGibbous,
  [EMoonPhase.Full]: messages.astral.fullMoon,
  [EMoonPhase.WaningGibbous]: messages.astral.waningGibbous,
  [EMoonPhase.LastQuarter]: messages.astral.lastQuarter,
  [EMoonPhase.WaningCrescent]: messages.astral.waningCrescent,
};

export const getPhaseMessage = (phase: EMoonPhase) =>
  PHASE_MESSAGES[phase as Exclude<EMoonPhase, EMoonPhase.COUNT>] ??
  // `astral.moon`. Falling back to a named phase would assert a specific WRONG
  // moon for an out-of-range value; the generic noun is the honest degradation.
  messages.astral.moon;
